import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import ModalShell, { inputClass, selectClass, textareaClass, labelClass, requiredMark, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error';
  const { data, status } = error.response;
  if (status === 422 && data.detail) {
    if (Array.isArray(data.detail)) { const e = data.detail[0]; return typeof e === 'object' ? `${e.loc?.[e.loc.length-1]||'Field'}: ${e.msg||'Invalid'}` : String(e); }
    if (typeof data.detail === 'string') return data.detail;
  }
  return data.detail || data.message || `Request failed (${status})`;
};

const SupplierPaymentForm = ({ suppliers, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    supplier_id: '',
    amount: '',
    payment_method: 'bank_transfer',
    payment_date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: '',
    purchase_order_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingPOs, setLoadingPOs] = useState(false);

  const selectedSupplier = suppliers.find(s => s.id === formData.supplier_id);

  useEffect(() => {
    if (formData.supplier_id) {
      setLoadingPOs(true);
      setPurchaseOrders([]);
      setFormData(prev => ({ ...prev, purchase_order_id: '' }));
      api.get(`/suppliers/${formData.supplier_id}/outstanding-pos`)
        .then(res => setPurchaseOrders(res.data))
        .catch(err => console.error('Error fetching POs:', err))
        .finally(() => setLoadingPOs(false));
    } else {
      setPurchaseOrders([]);
    }
  }, [formData.supplier_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.supplier_id) { setError('Select a supplier'); return; }
    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) { setError('Enter a valid amount'); return; }
    setLoading(true);
    try {
      const payload = { ...formData, amount };
      if (!formData.purchase_order_id) delete payload.purchase_order_id;
      await api.post('/supplier-payments', payload);
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (key, val) => setFormData({ ...formData, [key]: val });

  return (
    <ModalShell
      title="Record Supplier Payment"
      onClose={onClose}
      maxWidth="max-w-lg"
      dataTestId="supplier-payment-modal"
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors" data-testid="cancel-payment-form">Cancel</button>
          <button type="submit" form="supplier-payment-form" disabled={loading} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50" data-testid="save-payment-button">{loading ? 'Recording...' : 'Record Payment'}</button>
        </div>
      }
    >
      <form id="supplier-payment-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="payment-form-error">{error}</div>}

        <div className="space-y-4">
          <div>
            <label className={labelClass}>Supplier {requiredMark}</label>
            <select className={selectClass} value={formData.supplier_id} onChange={(e) => set('supplier_id', e.target.value)} required data-testid="payment-supplier-select">
              <option value="">Select Supplier</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name} (Balance: Rs. {parseFloat(s.balance).toLocaleString()})</option>)}
            </select>
          </div>

          {selectedSupplier && (
            <div className="p-3 bg-white rounded-lg border border-[#d1d5db] text-sm">
              <div className="flex justify-between"><span className="font-medium">Current Balance:</span><span className="text-[#EF4444] font-semibold">Rs. {parseFloat(selectedSupplier.balance).toLocaleString()}</span></div>
            </div>
          )}

          {formData.supplier_id && (
            <div>
              <label className={labelClass}>Allocate to Purchase Order <span className="text-[#6b7280] font-normal">(optional)</span></label>
              <select className={selectClass} value={formData.purchase_order_id} onChange={(e) => set('purchase_order_id', e.target.value)} data-testid="payment-po-select" disabled={loadingPOs}>
                <option value="">Unallocated (will apply to oldest PO first)</option>
                {loadingPOs && <option value="">Loading POs...</option>}
                {!loadingPOs && purchaseOrders.map(po => (
                  <option key={po.id} value={po.id}>{po.po_number} - Rs. {parseFloat(po.total_amount).toLocaleString()} ({po.status})</option>
                ))}
              </select>
              <p className="text-xs text-[#6b7280] mt-1">Leave blank to auto-allocate to the oldest outstanding PO (FIFO).</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Amount (PKR) {requiredMark}</label>
              <input type="number" min="0.01" step="0.01" className={inputClass} value={formData.amount} onChange={(e) => set('amount', e.target.value)} required data-testid="payment-amount-input" placeholder="Enter amount" />
            </div>
            <div>
              <label className={labelClass}>Payment Method</label>
              <select className={selectClass} value={formData.payment_method} onChange={(e) => set('payment_method', e.target.value)} data-testid="payment-method-select">
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Payment Date</label>
              <input type="date" className={inputClass} value={formData.payment_date} onChange={(e) => set('payment_date', e.target.value)} data-testid="payment-date-input" />
            </div>
            <div>
              <label className={labelClass}>Reference</label>
              <input type="text" className={inputClass} value={formData.reference} onChange={(e) => set('reference', e.target.value)} placeholder="Cheque #, Transaction ID" data-testid="payment-reference-input" />
            </div>
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea className={textareaClass} rows="2" value={formData.notes} onChange={(e) => set('notes', e.target.value)} data-testid="payment-notes-input" />
          </div>
        </div>
      </form>
    </ModalShell>
  );
};

export default SupplierPaymentForm;
